'use strict';
//***********************************************************************************
//***********************************************************************************
import { cn_pastille } from './cn_pastille';
import { cn_add, cn_box, cn_cart, cn_clone, cn_dist, cn_dot, cn_middle, cn_mul, cn_normal, cn_polar, cn_sub } from '../utils/cn_utilities';
import { cn_space } from '../model/cn_space';
import { cn_scene } from '../model/cn_scene';
import { cn_scene_controller } from './cn_scene_controller';
import { cn_roof_controller } from './cn_roof_controller';
import { cn_event_handler } from './cn_event_handler';
import { cn_mouse_event } from './cn_mouse_event';
import { cn_camera } from './cn_camera';
import { cn_number_input, cn_number_list_input } from './cn_inputs';
import { cn_snap } from './cn_snap';
import { cn_handler_resize } from './cn_handler_resize';
import { cn_handler_object } from './cn_handler_object';
import { cn_handler_rotate } from './cn_handler_rotate';
import { cn_handler_translate } from './cn_handler_translate';
import { cn_duplicator } from '../model/cn_duplicator';
import { cn_handler_wall_drag } from './cn_handler_wall_drag';
import { cn_handler_wall_distance } from './cn_handler_wall_distance';
import { cn_opening } from '../model/cn_opening';

/**
 * A handler to manipulate a grid of cloned elements
 * @class cn_duplicator_handler
 */
export class cn_duplicator_handler extends cn_event_handler {
    //*****************************************************************
    /**
     * Constructor
     * @param {cn_duplicator} duplicator
     * @param {cn_event_handler} parent
     */
    constructor(duplicator, parent) {
        super(parent);
        this.duplicator = duplicator;
        this._edit_box = null;
        this._wall_duplicator = this.duplicator.get_wall() != null;

        let handler_wall_drag = null;

        this.transaction_manager = duplicator.parent.building.transaction_manager;
        this._inner_object = new cn_handler_object();
        this._inner_handler = new cn_handler_resize(this._inner_object, this);
        this._handlers.push(this._inner_handler);
        this._inner_handler.set_handle_activity(2, !this._wall_duplicator);
        this._inner_handler.set_handle_activity(3, !this._wall_duplicator);
        this._inner_handler.rectangle_box = false;
        this._inner_handler.on("change", () => {
            this.duplicator.center = cn_clone(this._inner_object.center);
            this.duplicator.spacings[0] = this._inner_object.width / this.duplicator.cells[0];
            this.duplicator.spacings[1] = this._inner_object.height / this.duplicator.cells[1];
            this.transaction_manager.push_transaction("Modification de duplicateur", this.duplicator.ID);
            this.duplicator.update_elements(this.transaction_manager);
            if (handler_wall_drag) {
                let wall = this.duplicator.get_wall();
                handler_wall_drag.set_wall(wall, 0, wall.compute_abcissa(this.duplicator.center));
            }
            return false;
        });

        this._outer_object = new cn_handler_object();
        this._outer_handler = new cn_handler_resize(this._outer_object, this);
        this._handlers.push(this._outer_handler);
        this._outer_handler.rectangle_box = false;
        this._outer_handler.set_handle_activity(2, !this._wall_duplicator);
        this._outer_handler.set_handle_activity(3, !this._wall_duplicator);
        this._outer_handler.on("change", () => {
            let change = false;
            let new_x_cells = Math.round(this._outer_object.width / this.duplicator.spacings[0]) - 1;
            if (new_x_cells < 1) new_x_cells = 1;
            change ||= (new_x_cells != this.duplicator.cells[0]);
            this.duplicator.cells[0] = new_x_cells;
            let new_y_cells = Math.round(this._outer_object.height / this.duplicator.spacings[1]) - 1;
            if (new_y_cells < 1) new_y_cells = 1;
            change ||= (new_y_cells != this.duplicator.cells[1]);
            this.duplicator.cells[1] = new_y_cells;
            //if (change)
            //    this.duplicator.center = cn_clone(this._outer_object.center);
            this.transaction_manager.push_transaction("Modification de duplicateur", this.duplicator.ID);
            this.duplicator.update_elements(this.transaction_manager);
            if (this._edit_box) this._edit_box.elements = this.duplicator.elements;
            return false;
        });

        if (this._wall_duplicator) {
            let wall = this.duplicator.get_wall();
            handler_wall_drag = new cn_handler_wall_drag(this.duplicator.parent.storey, wall, 0, wall.compute_abcissa(this.duplicator.center));
            this._handlers.push(handler_wall_drag);
            handler_wall_drag.on("change", () => {
                this.transaction_manager.push_transaction("Modification de duplicateur", this.duplicator.ID);
                this.duplicator.center = cn_middle(handler_wall_drag.wall.compute_side_position_from_abcissa(handler_wall_drag.wall_position, 0), handler_wall_drag.wall.compute_side_position_from_abcissa(handler_wall_drag.wall_position, 1));
                this.duplicator.update_elements(this.transaction_manager, handler_wall_drag.wall, handler_wall_drag.wall_side);
            });
        }
        else {
            let handler_rotation = new cn_handler_rotate(this._outer_object, this);
            this._handlers.push(handler_rotation);
            handler_rotation.on("change", () => {
                this.transaction_manager.push_transaction("Modification de duplicateur", this.duplicator.ID);
                this.duplicator.orientation = this._outer_object.angle;
                this.duplicator.update_elements(this.transaction_manager);
                return false;
            });

            let translate_handler = new cn_handler_translate(this._inner_object, this);
            this._handlers.push(translate_handler);
            translate_handler.on("change", () => {
                this.transaction_manager.push_transaction("Modification de duplicateur", this.duplicator.ID);
                this.duplicator.center = cn_clone(this._inner_object.center);
                this.duplicator.update_elements(this.transaction_manager);
                return false;
            });
        }

        this._measure_handler = new cn_handler_wall_distance(this.duplicator.parent, this);
        this._handlers.push(this._measure_handler);
        this._measure_handler.on('change', (offset) => {
            this.transaction_manager.push_transaction("Déplacement de duplicateur", this.duplicator.ID);
            this.transaction_manager.push_item_set(this.duplicator, ['center',]);
            this.duplicator.center = cn_add(this.duplicator.center, offset);
            this.duplicator.update_elements(this.transaction_manager);
            this.call("full_refresh");
        });
    }

    /** Done here to avoid cylcic dependencies */
    set_edit_box(edit_box) {
        this._edit_box = edit_box;
        this._edit_box.elements = this.duplicator.elements;
        this._handlers.push(this._edit_box);

        let pastille_details = new cn_pastille([0, 0], "text.svg");
        pastille_details.svg_class = 'pastille_background white';
        pastille_details.title = 'Paramètres de dupliaction';
        pastille_details.clicked = () => {
            let number_inputs = [];
            number_inputs.push(new cn_number_input((this._wall_duplicator) ? 'Cellules' : 'Cellules H', this.duplicator.cells[0], "", 0, 1, 1000));
            if (!this._wall_duplicator)
                number_inputs.push(new cn_number_input('Cellules V', this.duplicator.cells[1], "", 0, 1, 1000));
            number_inputs.push(new cn_number_input((this._wall_duplicator) ? 'Espacement' : 'Espacement H', this.duplicator.spacings[0], "m", 2, 0.01, 100));
            if (!this._wall_duplicator)
                number_inputs.push(new cn_number_input('Espacement V', this.duplicator.spacings[1], "m", 2, 0.01, 100));
            let input = new cn_number_list_input('Paramètres', number_inputs, () => {
                this.transaction_manager.push_transaction("Paramètres de duplication");
                this.transaction_manager.push_item_set(this.duplicator, ["cells", "spacings"]);
                if (this._wall_duplicator) {
                    this.duplicator.cells[0] = number_inputs[0].value;
                    this.duplicator.spacings[0] = number_inputs[1].value;
                }
                else {
                    this.duplicator.cells[0] = number_inputs[0].value;
                    this.duplicator.cells[1] = number_inputs[1].value;
                    this.duplicator.spacings[0] = number_inputs[2].value;
                    this.duplicator.spacings[1] = number_inputs[3].value;
                }
                this.duplicator.update_elements(this.transaction_manager);
                this._edit_box.elements = this.duplicator.elements;
                this.call("change");
            });
            this.call('number_list_input', input);
        };
        edit_box.add_pastille(pastille_details);

        let pastille_check = new cn_pastille([0, 0], "check.svg");
        pastille_check.svg_class = 'pastille_background white';
        pastille_check.title = 'Fermer le duplicateur';
        pastille_check.clicked = () => { this.call("close"); };
        edit_box.add_pastille(pastille_check);

        edit_box.remove_align_pastille();
        
        this.on("delete_elements", () => {
            this.transaction_manager.push_transaction("Suppression de duplicateur");
            this.duplicator.remove(this.transaction_manager);
            this.duplicator = null;
            this.call("close");
            return true;
        });
    }

    draw(camera) {
        this._update_measure_handler();

        this._inner_object.center = this.duplicator.center;
        this._inner_object.width = this.duplicator.cells[0] * this.duplicator.spacings[0];
        this._inner_object.height = this.duplicator.cells[1] * this.duplicator.spacings[1];
        this._inner_object.angle = this.duplicator.orientation;
        this._inner_object.update();

        this._outer_object.center = this.duplicator.center;
        this._outer_object.width = (1 + this.duplicator.cells[0]) * this.duplicator.spacings[0];
        this._outer_object.height = (1 + this.duplicator.cells[1]) * this.duplicator.spacings[1];
        this._outer_object.angle = this.duplicator.orientation;
        this._outer_object.update();

        let html = "";
        for (var j = 0; j < this.duplicator.cells[1]; j++) {
            let y0 = -this._inner_object.height * 0.5 + j * this.duplicator.spacings[1];
            let y1 = y0 + this.duplicator.spacings[1];
            for (var i = 0; i < this.duplicator.cells[0]; i++) {
                let x0 = - this._inner_object.width * 0.5 + i * this.duplicator.spacings[0];
                let x1 = x0 + this.duplicator.spacings[0];
                let p0 = this._inner_object.local_to_global([x0, y0]);
                let p1 = this._inner_object.local_to_global([x1, y0]);
                let p2 = this._inner_object.local_to_global([x1, y1]);
                let p3 = this._inner_object.local_to_global([x0, y1]);
                html += camera.draw_path([p0, p1, p2, p3], "duplication_grid_inner", true);
            }
        }

        let vv = this._inner_object.vertices;
        html += camera.draw_path([vv[0], vv[1], vv[3], vv[2]], "duplication_grid_outer", true);

        vv = this._outer_object.vertices;
        html += camera.draw_path([vv[0], vv[1], vv[3], vv[2]], "duplication_grid_outer", true);
        html += super.draw(camera);
        return html;
    }

    _update_measure_handler() {
        this._measure_handler.segments = [];

        if (this.duplicator.elements.length == 0) return;
        if (!this.duplicator.elements[0]) return;
        if (this.duplicator.elements[0].constructor == cn_opening) {
            let wall = this.duplicator.get_wall();
            let x0 = this.duplicator.get_wall_position(this.duplicator.elements[0])[0];
            let x1 = this.duplicator.get_wall_position(this.duplicator.elements[this.duplicator.elements.length - 1])[1];
            this._measure_handler.segments.push([x0, 0, -1, wall]);
            this._measure_handler.segments.push([x0, 1, -1, wall]);
            this._measure_handler.segments.push([x1, 0, 1, wall]);
            this._measure_handler.segments.push([x1, 1, 1, wall]);
            return;
        }
        let center = this.duplicator.center;
        let dx = [Math.cos(this.duplicator.orientation), Math.sin(this.duplicator.orientation)];
        const dy = cn_normal(dx);
        let box = new cn_box();
        let vertices = [];
        this.duplicator.elements.forEach(elt => {
            let p = cn_sub(this.duplicator.get_element_position(elt), center);
            let sz = cn_duplicator.get_element_size(elt);
            box.enlarge_point([cn_dot(p, dx) - 0.5 * sz[0], cn_dot(p, dy) - 0.5 * sz[1]]);
            box.enlarge_point([cn_dot(p, dx) + 0.5 * sz[0], cn_dot(p, dy) + 0.5 * sz[1]]);
        });
        if (box.posmin) {
            vertices.push(box.posmin);
            vertices.push(cn_add(box.posmin, [box.size[0], 0]));
            vertices.push(cn_add(box.posmin, [0, box.size[1]]));
            vertices.push(cn_add(box.posmin, [box.size[0], box.size[1]]));
        }
        vertices = vertices.map(v => cn_add(center, cn_add(cn_mul(dx, v[0]), cn_mul(dy, v[1]))));
        if (vertices.length) {

            if (this.duplicator.get_wall()) {
                this._measure_handler.segments.push([vertices[0], vertices[2]]);
                this._measure_handler.segments.push([vertices[3], vertices[1]]);
            }
            else {
                this._measure_handler.segments.push([
                    vertices[0], vertices[2]
                ]);
                this._measure_handler.segments.push([
                    vertices[2], vertices[3]
                ]);
                this._measure_handler.segments.push([
                    vertices[3], vertices[1]
                ]);
                this._measure_handler.segments.push([
                    vertices[1], vertices[0]
                ]);

            }
        }
    }
}

